import React from 'react';

import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';

/** Debounce a changing value so list searches do not issue one query per keystroke. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Attach the returned ref to a sentinel at the end of a list. The latest
 * onLoadMore callback fires when the sentinel approaches the viewport.
 */
export function useInfiniteScroll(onLoadMore: () => void, enabled: boolean) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const onLoadMoreRef = React.useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  React.useEffect(() => {
    if (!enabled) return;
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMoreRef.current();
        }
      },
      { rootMargin: '120px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled]);

  return ref;
}

export function InfiniteScrollFooter(props: {
  hasNextPage: boolean;
  sentinelRef: React.Ref<HTMLDivElement>;
  error?: boolean;
  onRetry?: () => void;
  label?: string;
}) {
  if (!props.hasNextPage) return null;

  return (
    <div
      ref={props.sentinelRef}
      className="flex items-center justify-center gap-2 py-3 text-sm text-neutral-500 dark:text-neutral-400"
    >
      {props.error ? (
        <>
          <span>Unable to load more.</span>
          {props.onRetry && (
            <Button type="button" plain onClick={props.onRetry}>
              Try again
            </Button>
          )}
        </>
      ) : (
        <>
          <Spinner color="neutral" size="size-4" />
          {props.label ?? 'Loading more…'}
        </>
      )}
    </div>
  );
}
